/**
 * Management: people and their pay. Hours live on the timesheet.
 * A new rate starts on the chosen date; earlier days keep the previous one.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { Database } from '@cafe-pos/supabase';
import { formatAmd, resolveAppLocale } from '../locale.js';
import { rateOn, type PayType, type RatePeriod } from '../lib/wages.js';
import { supabase } from '../supabase.js';
import {
  Button,
  Callout,
  ConfirmDialog,
  IconButton,
  Modal,
  PageHeader,
  SegmentedControl,
} from '../ui/index.js';

type StaffRow = Database['public']['Tables']['staff']['Row'];
type PayRow = Database['public']['Tables']['staff_pay']['Row'];
type RateRow = Database['public']['Tables']['staff_rates']['Row'];

interface PersonForm {
  name: string;
  active: boolean;
  onTimesheet: boolean;
  sortOrder: string;
  payType: PayType;
  rate: string;
  effectiveFrom: string;
}

function asPayType(value: string | undefined): PayType {
  if (value === 'shift' || value === 'monthly') return value;
  return 'hourly';
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function periodsOf(rows: RateRow[]): RatePeriod[] {
  return rows.map((row) => ({
    effectiveFrom: row.effective_from,
    payType: asPayType(row.pay_type),
    rateAmd: row.rate_amd,
  }));
}

export function StaffView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const money = (n: number) => formatAmd(n, i18n.language);
  const today = isoDate(new Date());

  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [payById, setPayById] = useState<Record<string, PayRow>>({});
  const [ratesById, setRatesById] = useState<Record<string, RateRow[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [personId, setPersonId] = useState<'create' | string | null>(null);
  const [personForm, setPersonForm] = useState<PersonForm>(blankPerson(today));
  const [personSaving, setPersonSaving] = useState(false);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [rateToDelete, setRateToDelete] = useState<RateRow | null>(null);
  const [rateBusy, setRateBusy] = useState(false);

  async function load() {
    if (!supabase || !clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const [staffRes, payRes, rateRes] = await Promise.all([
      supabase
        .from('staff')
        .select('*')
        .eq('client_id', clientId)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true }),
      supabase.from('staff_pay').select('*').eq('client_id', clientId),
      supabase.from('staff_rates').select('*').eq('client_id', clientId),
    ]);
    const err = staffRes.error ?? payRes.error ?? rateRes.error;
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    const pays: Record<string, PayRow> = {};
    for (const row of payRes.data ?? []) pays[row.staff_id] = row;
    const rates: Record<string, RateRow[]> = {};
    for (const row of rateRes.data ?? []) (rates[row.staff_id] ??= []).push(row);
    setStaff(staffRes.data ?? []);
    setPayById(pays);
    setRatesById(rates);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  function currentOf(id: string): RatePeriod | null {
    const fromHistory = rateOn(periodsOf(ratesById[id] ?? []), today);
    if (fromHistory) return fromHistory;
    const pay = payById[id];
    if (!pay) return null;
    return { effectiveFrom: '', payType: asPayType(pay.pay_type), rateAmd: pay.rate_amd };
  }

  function openCreate() {
    setPersonForm(blankPerson(today));
    setPersonId('create');
    setError(null);
  }

  function openEdit(row: StaffRow) {
    const current = currentOf(row.id);
    setPersonForm({
      name: row.name,
      active: row.active,
      onTimesheet: row.on_timesheet,
      sortOrder: String(row.sort_order),
      payType: current?.payType ?? 'hourly',
      rate: current ? String(current.rateAmd) : '',
      effectiveFrom: today,
    });
    setPersonId(row.id);
    setError(null);
  }

  function onEffectiveChange(date: string) {
    if (!date) return;
    if (personId && personId !== 'create') {
      const rate = rateOn(periodsOf(ratesById[personId] ?? []), date);
      setPersonForm((form) => ({
        ...form,
        effectiveFrom: date,
        ...(rate ? { payType: rate.payType, rate: String(rate.rateAmd) } : {}),
      }));
      return;
    }
    setPersonForm((form) => ({ ...form, effectiveFrom: date }));
  }

  async function savePerson() {
    if (!supabase || !clientId || !personId) return;
    const name = personForm.name.trim();
    if (!name) {
      setError(t('staff.nameRequired'));
      return;
    }
    const onTimesheet = personForm.onTimesheet;
    const rate = Math.max(0, Math.round(Number(personForm.rate) || 0));
    const sortOrder = Math.round(Number(personForm.sortOrder) || 0);
    const effectiveFrom = personForm.effectiveFrom || today;
    setPersonSaving(true);
    setError(null);

    let staffId = personId;
    if (personId === 'create') {
      const { data, error: err } = await supabase
        .from('staff')
        .insert({
          client_id: clientId,
          name,
          active: personForm.active,
          on_timesheet: onTimesheet,
          sort_order: sortOrder,
        })
        .select('id')
        .single();
      if (err || !data) {
        setError(err?.message ?? t('staff.nameRequired'));
        setPersonSaving(false);
        return;
      }
      staffId = data.id;
    } else {
      const { error: err } = await supabase
        .from('staff')
        .update({ name, active: personForm.active, on_timesheet: onTimesheet, sort_order: sortOrder })
        .eq('id', personId);
      if (err) {
        setError(err.message);
        setPersonSaving(false);
        return;
      }
    }

    if (!onTimesheet) {
      setPersonSaving(false);
      setPersonId(null);
      void load();
      return;
    }

    const { error: rateErr } = await supabase.from('staff_rates').upsert(
      {
        staff_id: staffId,
        client_id: clientId,
        pay_type: personForm.payType,
        rate_amd: rate,
        effective_from: effectiveFrom,
      },
      { onConflict: 'staff_id,effective_from' },
    );
    if (rateErr) {
      setError(rateErr.message);
      setPersonSaving(false);
      return;
    }

    const { data: history, error: histErr } = await supabase
      .from('staff_rates')
      .select('*')
      .eq('staff_id', staffId);
    if (histErr) {
      setError(histErr.message);
      setPersonSaving(false);
      return;
    }
    const current = rateOn(periodsOf(history ?? []), today);
    const { error: payErr } = await supabase.from('staff_pay').upsert({
      staff_id: staffId,
      client_id: clientId,
      pay_type: current?.payType ?? 'hourly',
      rate_amd: current?.rateAmd ?? 0,
    });
    if (payErr) {
      setError(payErr.message);
      setPersonSaving(false);
      return;
    }

    setPersonSaving(false);
    setPersonId(null);
    void load();
  }

  async function confirmDeleteRate() {
    if (!supabase || !clientId || !rateToDelete || personId === 'create' || !personId) return;
    setRateBusy(true);
    setError(null);
    const { error: err } = await supabase.from('staff_rates').delete().eq('id', rateToDelete.id);
    if (err) {
      setError(err.message);
      setRateBusy(false);
      return;
    }
    const remaining = (ratesById[personId] ?? []).filter((row) => row.id !== rateToDelete.id);
    const current = rateOn(periodsOf(remaining), today);
    const { error: payErr } = await supabase.from('staff_pay').upsert({
      staff_id: personId,
      client_id: clientId,
      pay_type: current?.payType ?? 'hourly',
      rate_amd: current?.rateAmd ?? 0,
    });
    if (payErr) {
      setError(payErr.message);
      setRateBusy(false);
      return;
    }
    setRatesById((prev) => ({ ...prev, [personId]: remaining }));
    setPayById((prev) => ({
      ...prev,
      [personId]: {
        staff_id: personId,
        client_id: clientId,
        pay_type: current?.payType ?? 'hourly',
        rate_amd: current?.rateAmd ?? 0,
        updated_at: new Date().toISOString(),
      },
    }));
    setRateBusy(false);
    setRateToDelete(null);
  }

  async function confirmDelete() {
    if (!supabase || !deleteId) return;
    setDeleteBusy(true);
    const { error: err } = await supabase.from('staff').delete().eq('id', deleteId);
    setDeleteBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setDeleteId(null);
    void load();
  }

  const query = search.trim().toLowerCase();
  const rows = staff.filter((person) => !query || person.name.toLowerCase().includes(query));
  const deletePerson = staff.find((person) => person.id === deleteId) ?? null;
  const rateHistory =
    personId && personId !== 'create'
      ? [...(ratesById[personId] ?? [])].sort((a, b) => b.effective_from.localeCompare(a.effective_from))
      : [];
  const inForceFrom = rateOn(periodsOf(rateHistory), today)?.effectiveFrom ?? '';

  function payPhrase(type: PayType): string {
    if (type === 'shift') return t('staff.payShift');
    if (type === 'monthly') return t('staff.payMonthly');
    return t('staff.payHourly');
  }

  function rateSuffix(type: PayType): string {
    if (type === 'shift') return t('staff.perShift');
    if (type === 'monthly') return t('staff.perMonth');
    return t('staff.perHour');
  }

  function formatDay(effectiveFrom: string): string {
    if (!effectiveFrom) return '';
    const y = Number(effectiveFrom.slice(0, 4));
    const m = Number(effectiveFrom.slice(5, 7));
    const d = Number(effectiveFrom.slice(8, 10));
    return new Date(y, m - 1, d).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  function sinceLabel(effectiveFrom: string): string {
    const label = formatDay(effectiveFrom);
    return label ? t('staff.since', { date: label }) : '';
  }

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('staff.title')}
        subtitle={t('staff.subtitle')}
        actions={
          <Button variant="primary" icon={<Plus className="ui-i" />} onClick={openCreate}>
            {t('staff.add')}
          </Button>
        }
      />

      {error && !personId && <Callout tone="danger">{error}</Callout>}

      <div className="panel">
        <div className="add-form" style={{ marginBottom: 8 }}>
          <input
            type="search"
            placeholder={t('staff.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
        </div>

        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div className="empty-state">{search ? t('staff.noMatch') : t('staff.empty')}</div>
        ) : (
          <table className="table table-cards">
            <thead>
              <tr>
                <th>{t('staff.colName')}</th>
                <th>{t('staff.colPay')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((person) => {
                const current = currentOf(person.id);
                const type = current?.payType ?? 'hourly';
                return (
                  <tr key={person.id} style={!person.active ? { opacity: 0.55 } : undefined}>
                    <td className="table-cards-title" data-label={t('staff.colName')}>
                      {person.name}
                      {!person.on_timesheet && (
                        <div className="muted" style={{ fontSize: 13 }}>
                          {t('staff.compOnly')}
                        </div>
                      )}
                    </td>
                    <td data-label={t('staff.colPay')}>
                      {payPhrase(type)}
                      <div className="muted" style={{ fontSize: 13 }}>
                        {money(current?.rateAmd ?? 0)} {rateSuffix(type)}
                        {current?.effectiveFrom ? ` · ${sinceLabel(current.effectiveFrom)}` : ''}
                      </div>
                    </td>
                    <td className="table-cards-actions">
                      <div className="row-actions">
                        <IconButton label={t('common.edit')} onClick={() => openEdit(person)}>
                          <Pencil className="ui-i" />
                        </IconButton>
                        <IconButton
                          label={t('common.delete')}
                          danger
                          onClick={() => setDeleteId(person.id)}
                        >
                          <Trash2 className="ui-i" />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={personId != null}
        onClose={() => {
          if (!personSaving) setPersonId(null);
        }}
        title={personId === 'create' ? t('staff.add') : t('staff.edit')}
        footer={
          <>
            <Button variant="secondary" disabled={personSaving} onClick={() => setPersonId(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" disabled={personSaving} onClick={() => void savePerson()}>
              {personSaving ? '…' : t('common.save')}
            </Button>
          </>
        }
      >
        {error && personId && <Callout tone="danger">{error}</Callout>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label className="form-field">
            <span className="form-label">
              {t('staff.colName')}
              <span className="form-required">*</span>
            </span>
            <input
              value={personForm.name}
              onChange={(e) => setPersonForm({ ...personForm, name: e.target.value })}
              autoFocus
            />
          </label>
          {personForm.onTimesheet && (
            <>
              <div className="form-field">
                <span className="form-label">{t('staff.colPay')}</span>
                <SegmentedControl
                  ariaLabel={t('staff.colPay')}
                  value={personForm.payType}
                  onChange={(payType) => setPersonForm({ ...personForm, payType })}
                  options={[
                    { value: 'hourly', label: t('staff.payHourly') },
                    { value: 'shift', label: t('staff.payShift') },
                    { value: 'monthly', label: t('staff.payMonthly') },
                  ]}
                />
              </div>
              <label className="form-field">
                <span className="form-label">
                  {t('staff.rate')} {rateSuffix(personForm.payType)}
                </span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={personForm.rate}
                  onChange={(e) => setPersonForm({ ...personForm, rate: e.target.value })}
                />
              </label>
              <label className="form-field">
                <span className="form-label">{t('staff.effectiveFrom')}</span>
                <input
                  type="date"
                  value={personForm.effectiveFrom}
                  onChange={(e) => onEffectiveChange(e.target.value)}
                />
              <span className="muted" style={{ fontSize: 13 }}>
                {t('staff.effectiveHint')}
              </span>
            </label>
              {rateHistory.length > 0 && (
                <div className="form-field">
                  <span className="form-label">{t('staff.rateHistory')}</span>
                  <div className="staff-rate-history">
                    {rateHistory.map((row) => (
                      <div className="staff-rate-history-row" key={row.id}>
                        <span>{formatDay(row.effective_from)}</span>
                        <span>
                          {money(row.rate_amd)} {rateSuffix(asPayType(row.pay_type))}
                          {row.effective_from === inForceFrom ? ` · ${t('staff.rateInForce')}` : ''}
                        </span>
                        <IconButton
                          label={t('common.delete')}
                          danger
                          size="sm"
                          disabled={rateBusy}
                          onClick={() => setRateToDelete(row)}
                        >
                          <Trash2 className="ui-i" />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <label className="form-field">
                <span className="form-label">{t('staff.sort')}</span>
                <input
                  type="number"
                  value={personForm.sortOrder}
                  onChange={(e) => setPersonForm({ ...personForm, sortOrder: e.target.value })}
                />
              </label>
              <span className="muted" style={{ fontSize: 13 }}>
                {t('staff.onTimesheetHint')}
              </span>
            </>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={personForm.onTimesheet}
              onChange={(e) => setPersonForm({ ...personForm, onTimesheet: e.target.checked })}
            />
            {t('staff.onTimesheet')}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={personForm.active}
              onChange={(e) => setPersonForm({ ...personForm, active: e.target.checked })}
            />
            {t('staff.active')}
          </label>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteId != null}
        busy={deleteBusy}
        onClose={() => {
          if (!deleteBusy) setDeleteId(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={t('staff.deleteTitle', { name: deletePerson?.name ?? '' })}
        message={t('staff.deleteBody')}
        confirmLabel={t('common.delete')}
      />

      <ConfirmDialog
        open={rateToDelete != null}
        busy={rateBusy}
        onClose={() => {
          if (!rateBusy) setRateToDelete(null);
        }}
        onConfirm={() => void confirmDeleteRate()}
        title={t('staff.rateDeleteTitle', { date: formatDay(rateToDelete?.effective_from ?? '') })}
        message={t('staff.rateDeleteBody')}
        confirmLabel={t('common.delete')}
      />
    </section>
  );
}

function blankPerson(today: string): PersonForm {
  return {
    name: '',
    active: true,
    onTimesheet: true,
    sortOrder: '0',
    payType: 'hourly',
    rate: '',
    effectiveFrom: today,
  };
}
